import { errorToast } from "lib/toast";

interface GeminiResponse {
  candidates?: Array<{
    content?: {
      parts?: Array<{
        text?: string;
      }>;
    };
  }>;
}

export class AIService {
  private static instance: AIService;
  private readonly apiKey: string | undefined;
  private readonly apiEndpoint =
    "https://generativelanguage.googleapis.com/v1beta/models/gemini-3-flash-preview:generateContent";

  private constructor() {
    this.apiKey = process.env.REACT_APP_GEMINI_API_KEY;
  }

  public static getInstance(): AIService {
    if (!AIService.instance) {
      AIService.instance = new AIService();
    }
    return AIService.instance;
  }

  public trimHTMLResponse(htmlResponse: string) {
    // Remove leading/trailing backticks
    let trimmed = htmlResponse.trim().replace(/^```html|```$/g, "");

    // Remove leading/trailing HTML tags
    trimmed = trimmed.replace(/^\s*<p>/, "").replace(/<\/p>\s*$/, "");

    return trimmed;
  }

  private async makeRequest(prompt: string): Promise<string | null> {
    try {
      if (!this.apiKey) {
        throw new Error("Gemini API key is not configured");
      }

      const response = await fetch(`${this.apiEndpoint}?key=${this.apiKey}`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          contents: [
            {
              parts: [{ text: prompt }],
            },
          ],
        }),
      });

      if (!response.ok) {
        throw new Error("Failed to get AI suggestion");
      }

      const data: GeminiResponse = await response.json();
      return data.candidates?.[0]?.content?.parts?.[0]?.text ?? null;
    } catch (error) {
      console.error("AI Service Error:", error);
      errorToast(
        error instanceof Error ? error.message : "Failed to get AI suggestion",
      );
      return null;
    }
  }
  public async generateSEOMetadata(
    type: "title" | "description",
    context: string,
  ): Promise<string | null> {
    const prompt = `Generate an SEO-friendly meta ${type} for the following brand on Cusorcart Marketplace: ${context}. 
  The meta ${type} should be ${
    type === "title" ? "under 60" : "under 160"
  } characters and optimized for search engines.
  Ensure high relevance and keyword optimization.
  IMPORTANT: The result MUST include "Cusorcart" or "Cusorcart Marketplace".
  Return only the ${type} text without any additional context or formatting.`;

    return this.makeRequest(prompt);
  }

  public async generateProductDescription(
    name: string,
    category: string,
    description: string,
  ): Promise<string | null> {
    const prompt = `You are acting as an expert e-commerce copywriter.  
      Write a clear, informative, and SEO-friendly product description for an online marketplace product details page.  

      You will be given:  
      - Product name: ${name}  
      - Product category: ${category}  
      - Initial seller input: ${description || "None provided"}  

      Your goal is to:  
      1. Write an accurate and well-structured product description.  
      2. Highlight the product’s main features, functions, and benefits.  
      3. Keep the language simple, straightforward, and easy for online shoppers to understand.  
      4. Match the tone to the category (e.g., professional for tech, warm for fashion, practical for tools).  
      5. Use bullet points only if the category typically benefits from them (e.g., electronics, appliances, tools).  
      6. Ensure the description is relevant and specific to the product category: ${category}.  

      Output format:  
      - Return only the finalized product description in **HTML format** (paragraphs, headings, lists where needed).  
      - Do not include markdown, notes, or extra commentary.`;

    const response = await this.makeRequest(prompt);
    if (!response) {
      return null;
    }

    return this.trimHTMLResponse(response);
  }

  // Add more AI generation methods as needed
}
